// ./src/components/generic/FontSizeSelector/FontSizeSelector.tsx
import React from "react";
import { Select, SelectProps } from "@mantine/core";

const FONT_SIZE_OPTIONS = [
  {
    value: "inherit",
    label: "Inherit",
    description: "",
  },
  {
    value: "0.75rem",
    label: "Extra Small",
    description: "0.75rem (12px at default size)",
  },
  {
    value: "0.875rem",
    label: "Small",
    description: "0.875rem (14px at default size)",
  },
  {
    value: "1rem",
    label: "Medium",
    description: "1rem (16px at default size)",
  },
  {
    value: "1.25rem",
    label: "Large",
    description: "1.25rem (20px at default size)",
  },
  {
    value: "1.5rem",
    label: "Extra Large",
    description: "1.5rem (24px at default size)",
  },
  {
    value: "2rem",
    label: "2X Large",
    description: "2rem (32px at default size)",
  },
  {
    value: "3rem",
    label: "3X Large",
    description: "3rem (48px at default size)",
  },
  {
    value: "4rem",
    label: "4X Large",
    description: "4rem (64px at default size)",
  },
  {
    value: "5rem",
    label: "5X Large",
    description: "5rem (80px at default size)",
  },
] as const;

export interface FontSizeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current font size value in rem */
  value: string | null | undefined;

  /** Callback when font size changes */
  onChange: (value: string | null) => void;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;

  /** Allow custom font size input */
  allowCustom?: boolean;
}

export const FontSizeSelector: React.FC<FontSizeSelectorProps> = ({
  value,
  onChange,
  withDescriptions = false,
  allowCustom = false,
  placeholder = "Select font size",
  label,
  description,
  error,
  ...selectProps
}) => {
  // Prepare select data with optional descriptions
  let selectData: any[] = FONT_SIZE_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // If allowCustom and value is not in presets, add it to the data
  if (allowCustom && value) {
    const isPreset = FONT_SIZE_OPTIONS.some((opt) => opt.value === value);
    if (!isPreset) {
      selectData = [
        ...selectData,
        {
          value,
          label: `Custom: ${value}`,
        },
      ];
    }
  }

  return (
    <Select
      {...selectProps}
      label={label}
      description={description}
      error={error}
      data={selectData}
      value={value || null}
      onChange={onChange}
      placeholder={placeholder}
      searchable={allowCustom}
      clearable={false}
      allowDeselect={false}
    />
  );
};

// Export font size options for use in other components
export { FONT_SIZE_OPTIONS };
